import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, CreditCard, Zap, Calendar } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { useStripePayment } from '@/hooks/useStripePayment';

interface BillingSectionProps {
  teamId: string;
}

export const BillingSection = ({ teamId }: BillingSectionProps) => {
  const { subscriptionStatus, loading } = useSubscription(teamId);
  const { openPayment } = useStripePayment();

  const handleManageBilling = () => {
    window.open('https://billing.stripe.com/p/login/28o9DldVb8y0ekM8ww', '_blank');
  };

  const handleUpgradeNow = () => {
    openPayment(teamId);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Billing & Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading billing information...</p>
        </CardContent>
      </Card>
    );
  }

  // Show trial interface for teams with active trials
  if (subscriptionStatus?.isTrialActive && !subscriptionStatus?.hasSubscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Free Trial Active
          </CardTitle>
          <CardDescription>
            You're currently on a free trial of our partner platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {subscriptionStatus.trialDaysRemaining} days remaining
              </Badge>
            </div>
            
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border">
              <h4 className="font-medium text-sm mb-2">Ready to unlock the full partner experience?</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Upgrade now to continue accessing all partner features when your trial expires.
              </p>
              <Button 
                onClick={handleUpgradeNow}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Zap className="h-4 w-4 mr-2" />
                Upgrade Now
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Your trial includes access to all partner platform features. After the trial period, 
              you'll need an active subscription to continue using these features.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show regular billing interface for teams with subscriptions
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Billing & Subscription
        </CardTitle>
        <CardDescription>
          Manage your team's subscription, payment methods, and billing history
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {subscriptionStatus?.hasSubscription && (
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="default" className="bg-green-100 text-green-800">
                Active Subscription
              </Badge>
            </div>
          )}
          
          <p className="text-sm text-muted-foreground">
            Access your Stripe customer portal to manage your subscription, update payment methods, 
            download invoices, and view billing history.
          </p>
          <Button 
            onClick={handleManageBilling}
            className="w-full sm:w-auto"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Manage Billing
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};